/**
 * Built-in seed roster for the XAES Phase Zero client-side login.
 *
 * ⚠️  Plaintext on purpose. Browser-side bcrypt was considered and rejected
 *     (see plans/REFACTOR_PLAN.md Risk 11): the hash ships in the JS bundle,
 *     is trivially extractable from DevTools, and the seed passwords are
 *     short enough that an offline dictionary attack runs in seconds.
 *     Hashing here would be theatre. Real auth lands via ZITADEL in Phase 7,
 *     which replaces this whole module.
 *
 * Until then: treat the whole app as public to anyone with the URL. Login
 * is a UX nicety (per-user role gating), not a security boundary.
 *
 * Adapted from `Loveable v1/src/content/users.ts` — same shape and seed
 * data, with the per-user `background` image field dropped (not yet wired
 * in this build).
 */

export interface UserProfile {
  username:    string;
  password:    string;
  displayName: string;
  role:        string;
  /**
   * Stable OIDC subject (`sub`) when signed in via ZITADEL. Preferred
   * for client-side persistence keys (chat history, etc.) because
   * `preferred_username` can change while `sub` does not.
   */
  subject?:    string;
  /**
   * Seed-mode affiliation: ``"dxc"`` (home) or a client registry id.
   * ZITADEL multi-client creates client users via the BFF instead.
   */
  affiliation?: string;
  /**
   * ZITADEL resident-org id (multi-client). Used by `switch:client` to
   * distinguish DXC home users from client-org users. Seed mode leaves
   * this unset and relies on ``affiliation`` instead.
   */
  orgId?: string;
}

const STORAGE_KEY = "xaes-users";

const builtInUsers: UserProfile[] = [
  { username: "facilitator", password: "xaes-facilitate", displayName: "Facilitator", role: "MISSION FACILITATOR" },
  { username: "analyst",     password: "xaes-analyse",    displayName: "Analyst",     role: "DATA ANALYST"        },
  { username: "observer",    password: "xaes-observe",    displayName: "Observer",    role: "MISSION OBSERVER"    },
  { username: "director",    password: "xaes-direct",     displayName: "Director",    role: "MISSION DIRECTOR"    },
  { username: "engineer",    password: "xaes-engineer",   displayName: "Engineer",    role: "SYSTEMS ENGINEER"    },
];

export const availableRoles = [
  "MISSION FACILITATOR",
  "DATA ANALYST",
  "MISSION OBSERVER",
  "MISSION DIRECTOR",
  "SYSTEMS ENGINEER",
];

function isValidUserProfile(u: unknown): u is UserProfile {
  if (typeof u !== "object" || u === null) return false;
  const r = u as Record<string, unknown>;
  return (
    typeof r.username === "string"    && r.username.length    > 0 &&
    typeof r.password === "string"    && r.password.length    > 0 &&
    typeof r.displayName === "string" && r.displayName.length > 0 &&
    typeof r.role === "string"        && r.role.length        > 0 &&
    (r.affiliation === undefined || typeof r.affiliation === "string")
  );
}

export function loadUsers(): UserProfile[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return builtInUsers;
    const parsed: unknown = JSON.parse(stored);
    if (Array.isArray(parsed) && parsed.every(isValidUserProfile)) return parsed;
    console.warn("Invalid user data in localStorage; falling back to seed roster.");
    return builtInUsers;
  } catch (err) {
    console.warn("Failed to load users:", err);
    return builtInUsers;
  }
}

export function saveUsers(users: UserProfile[]): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(users));
    return true;
  } catch (err) {
    console.error("Failed to save users:", err);
    return false;
  }
}

export function validatePassword(password: string): string | null {
  if (password.length < 8) return "Password must be 8+ chars with uppercase, lowercase, and a number.";
  if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/\d/.test(password)) {
    return "Password must contain uppercase, lowercase, and a number.";
  }
  return null;
}

export function validateUsername(username: string): string | null {
  if (username.length < 3 || !/^[a-z0-9_-]+$/.test(username)) {
    return "Username must be 3+ chars, alphanumeric/dash/underscore only.";
  }
  return null;
}
